import { useCallback, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import UserContext from "./UserContext";
import EffectiveAccessContext from "./EffectiveAccessContext";
import { resolveEffectivePageAccessList, hasAnyApplicationReturnPermission } from "common/Utilities/EffectiveAccess.utils";

const EffectiveAccessProvider = ({ children }) => {
  const { userProfile } = useContext(UserContext);
  const profile = userProfile?.profile ?? {};
  const effectiveAccess = profile?.effectiveAccess ?? null;
  const usesEffectiveAccess = Boolean(profile?.sessionFromMe && effectiveAccess);

  const pageAccessList = useMemo(() => {
    if (!usesEffectiveAccess) {
      return [];
    }
    return resolveEffectivePageAccessList(effectiveAccess);
  }, [usesEffectiveAccess, effectiveAccess]);

  const pageAccessByKey = useMemo(
    () => new Map(pageAccessList.map((page) => [page.pageKey, page])),
    [pageAccessList]
  );

  const canAccessPage = useCallback(
    (pageKey) => {
      if (!usesEffectiveAccess || !pageKey) {
        return true;
      }
      return pageAccessByKey.get(pageKey)?.hasAccess ?? false;
    },
    [usesEffectiveAccess, pageAccessByKey]
  );

  const filterTabsByAccess = useCallback(
    (tabs = []) => {
      if (!usesEffectiveAccess) {
        return tabs;
      }
      return tabs.filter((tab) => {
        if (!tab?.pageKey) {
          return true;
        }
        return canAccessPage(tab.pageKey);
      });
    },
    [usesEffectiveAccess, canAccessPage]
  );

  const canReturnToApplication = useCallback(() => {
    if (!usesEffectiveAccess) {
      return true;
    }
    return hasAnyApplicationReturnPermission(effectiveAccess);
  }, [usesEffectiveAccess, effectiveAccess]);

  const value = useMemo(
    () => ({
      usesEffectiveAccess,
      effectiveAccess,
      pageAccessList,
      canAccessPage,
      filterTabsByAccess,
      canReturnToApplication,
    }),
    [
      usesEffectiveAccess,
      effectiveAccess,
      pageAccessList,
      canAccessPage,
      filterTabsByAccess,
      canReturnToApplication,
    ]
  );

  return (
    <EffectiveAccessContext.Provider value={value}>
      {children}
    </EffectiveAccessContext.Provider>
  );
};

EffectiveAccessProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default EffectiveAccessProvider;
